import React, { useState, useEffect } from 'react';
import { ShieldCheck, Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { responsabiliAPI } from '../../services/api';

const emptyForm = { name: '', role: '', email: '', phone: '', is_custodian: false };

export const ResponsabiliWhistleblowingCard = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await responsabiliAPI.getAll();
      setItems(res.data);
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Nome ed email sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      await responsabiliAPI.create({ ...form, role: form.role || 'OdV' });
      toast.success('Responsabile aggiunto');
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rimuovere questo responsabile?')) return;
    try {
      await responsabiliAPI.delete(id);
      toast.success('Responsabile rimosso');
      load();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  return (
    <Card className="glass-card" data-testid="responsabili-card">
      <CardHeader>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <CardTitle className="text-2xl">OdV / Custodi Whistleblowing</CardTitle>
            <CardDescription>
              Chi riceve e gestisce le segnalazioni. Le email indicate qui sono autorizzate all'accesso via OTP
              e usate come destinatari se le email OdV non sono impostate nella configurazione SMTP.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="responsabili-empty">
            Nessun OdV configurato: le segnalazioni non verranno assegnate a nessuno.
          </p>
        ) : (
          <div className="space-y-2" data-testid="responsabili-list">
            {items.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/40" data-testid={`responsabile-${r.id}`}>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {r.name}
                    <Badge variant={r.is_custodian ? 'default' : 'secondary'}>
                      {r.is_custodian ? 'Custode / Amministratore' : 'OdV'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {r.role} · {r.email}{r.phone ? ` · ${r.phone}` : ''}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleDelete(r.id)}
                  data-testid={`delete-responsabile-${r.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleCreate} className="grid md:grid-cols-2 gap-4" data-testid="responsabile-form">
          <div>
            <Label htmlFor="resp-name">Nome e Cognome *</Label>
            <Input
              id="resp-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2"
              data-testid="responsabile-name-input"
            />
          </div>
          <div>
            <Label htmlFor="resp-email">Email *</Label>
            <Input
              id="resp-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2"
              data-testid="responsabile-email-input"
            />
          </div>
          <div>
            <Label htmlFor="resp-role">Ruolo</Label>
            <Input
              id="resp-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="Es: Organismo di Vigilanza"
              className="mt-2"
              data-testid="responsabile-role-input"
            />
          </div>
          <div>
            <Label htmlFor="resp-phone">Telefono</Label>
            <Input
              id="resp-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-2"
              data-testid="responsabile-phone-input"
            />
          </div>
          <label className="flex items-center gap-3 text-sm md:col-span-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_custodian}
              onChange={(e) => setForm({ ...form, is_custodian: e.target.checked })}
              className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              data-testid="responsabile-custodian-checkbox"
            />
            Custode/amministratore: riceve le segnalazioni della categoria "contro OdV"
          </label>
          <div className="md:col-span-2">
            <Button type="submit" disabled={saving} data-testid="add-responsabile-btn">
              <Plus className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Aggiungi responsabile'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ResponsabiliWhistleblowingCard;
